// 🚀 90s Wedding Game - Main Entry Point
// Clean initialization for retro Mario-style platformer

import './style.css';
import { createGame } from './game/Game.js';
import { GAME_CONFIG } from './game/config.js';

console.log('🎮 90s Wedding Game Loading...');
console.log(`📋 Version: ${GAME_CONFIG.VERSION}`);

class GameLauncher {
  constructor() {
    this.game = null;
    this.isReady = false;
    
    this.init();
  }

  async init() {
    try {
      console.log('🎮 90s Wedding Game Loading...');
      
      // Wait for fonts to load
      await this.loadFonts();
      
      // Wait for DOM to be ready
      await this.waitForDOM();
      
      // ALTERNATIVE: Generate manifest dynamically
      this.createDynamicManifest();
      
      // Initialize the game
      this.startGame();
      
    } catch (error) {
      console.error('❌ Failed to initialize game:', error);
      this.showStartupError(error);
    }
  }

  // File: src/main.js (update the createDynamicManifest method)

// NEW: Create manifest dynamically with proper URLs
createDynamicManifest() {
  // Get the current origin (works for localhost and production)
  const baseUrl = window.location.origin;
  
  const manifestData = {
    name: "Pablo & Bettina's Wedding Game",
    short_name: "Wedding Game",
    description: "Retro 90s Mario-style wedding platformer game",
    start_url: baseUrl + "/", // FIXED: Use full URL
    scope: baseUrl + "/", // FIXED: Use full URL  
    display: "standalone",
    background_color: "#5C94FC",
    theme_color: "#FF69B4",
    orientation: "any",
    categories: ["games", "entertainment"],
    icons: [
      {
        src: "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 192 192'><rect width='192' height='192' fill='%235C94FC'/><text x='96' y='130' font-size='100' text-anchor='middle'>💒</text></svg>",
        sizes: "192x192",
        type: "image/svg+xml",
        purpose: "any maskable"
      },
      {
        src: "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'><rect width='512' height='512' fill='%235C94FC'/><text x='256' y='350' font-size='280' text-anchor='middle'>💒</text></svg>",
        sizes: "512x512",
        type: "image/svg+xml", 
        purpose: "any maskable"
      }
    ]
  };

  // Create manifest blob and URL
  const manifestBlob = new Blob([JSON.stringify(manifestData, null, 2)], {
    type: 'application/json'
  });
  const manifestURL = URL.createObjectURL(manifestBlob);

  // Update or create manifest link
  let manifestLink = document.querySelector('link[rel="manifest"]');
  if (!manifestLink) {
    manifestLink = document.createElement('link');
    manifestLink.rel = 'manifest';
    document.head.appendChild(manifestLink);
  }
  manifestLink.href = manifestURL;

  console.log('📱 Dynamic manifest created with proper URLs:', manifestData.start_url);
}


  async loadFonts() {
    return new Promise((resolve) => {
      // Check if Press Start 2P font is loaded
      if (document.fonts) {
        document.fonts.ready.then(() => {
          console.log('✅ Fonts loaded');
          resolve();
        });
      } else {
        // Fallback for older browsers
        setTimeout(resolve, 1000);
      }
    });
  }

  async waitForDOM() {
    return new Promise((resolve) => {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', resolve);
      } else {
        resolve();
      }
    });
  }

  startGame() {
    console.log('🎮 Starting Wedding Game...');
    
    try {
      // Create the game
      this.game = createGame();
      this.isReady = true;
      
      // Setup global error handling
      this.setupErrorHandling();
      
      console.log('✅ Wedding Game launched successfully!');
      
      // Test backend connection
      this.testBackendConnection();
      
    } catch (error) {
      console.error('❌ Failed to start game:', error);
      this.showStartupError(error);
    }
  }

  setupErrorHandling() {
    // Global error handler
    window.addEventListener('error', (event) => {
      console.error('🌐 Runtime error:', event.error);
      this.showGameError(event.error);
    });

    // Unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      console.error('🌐 Unhandled promise rejection:', event.reason);
      this.showGameError(event.reason);
    });
  }

  async testBackendConnection() {
    // Commented out to reduce console warnings when no backend
    /*
    try {
      console.log('🔗 Testing backend connection...');
      
      const response = await fetch(`${GAME_CONFIG.API.BASE_URL}/health`, {
        method: 'GET',
        signal: AbortSignal.timeout(3000) // 3 second timeout
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Backend connected:', data);
      } else {
        console.warn('⚠️ Backend returned error:', response.status);
      }
      
    } catch (error) {
      console.warn('⚠️ Backend connection failed - using offline mode:', error.message);
    }
    */
    console.log('📴 Running in offline mode - scores will be saved locally');
  }

  showStartupError(error) {
    // Hide loading screen
    const loading = document.getElementById('loading');
    if (loading) {
      loading.style.display = 'none';
    }
    
    // Show startup error
    const errorHTML = `
      <div style="
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(45deg, #FF69B4, #4ECDC4);
        color: white;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        font-family: 'Press Start 2P', monospace;
        text-align: center;
        padding: 20px;
        z-index: 10000;
      ">
        <h1 style="font-size: 24px; margin-bottom: 30px;">💔 STARTUP ERROR</h1>
        <p style="font-size: 12px; margin-bottom: 20px; max-width: 600px; line-height: 1.8;">
          Oops! The wedding game couldn't start properly. 
          This might be a browser compatibility issue.
        </p>
        <div style="
          background: rgba(0,0,0,0.5);
          padding: 15px;
          margin-bottom: 30px;
          font-size: 8px;
          max-width: 500px;
          word-break: break-word;
          border-radius: 5px;
        ">
          Error: ${error.message || error}
        </div>
        <div style="display: flex; gap: 20px; flex-wrap: wrap; justify-content: center;">
          <button onclick="location.reload()" style="
            font-family: 'Press Start 2P', monospace;
            font-size: 10px;
            padding: 15px 25px;
            background: #32CD32;
            color: white;
            border: 3px solid #228B22;
            cursor: pointer;
            border-radius: 5px;
          ">🔄 TRY AGAIN</button>
          <button onclick="window.open('https://caniuse.com/webgl', '_blank')" style="
            font-family: 'Press Start 2P', monospace;
            font-size: 10px;
            padding: 15px 25px;
            background: #4169E1;
            color: white;
            border: 3px solid #1E90FF;
            cursor: pointer;
            border-radius: 5px;
          ">📱 CHECK BROWSER</button>
        </div>
        <p style="font-size: 8px; margin-top: 30px; opacity: 0.8; max-width: 400px;">
          Try using Chrome, Firefox, Safari, or Edge for the best experience.
          Make sure your browser supports WebGL.
        </p>
      </div>
    `;
    
    document.body.innerHTML = errorHTML;
  }

  showGameError(error) {
    // Only show if we haven't already shown a startup error
    if (!this.isReady) return;
    
    console.error('🎮 Game runtime error:', error);
    
    // Create error notification
    const notification = document.createElement('div');
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #FF6347;
      color: white;
      padding: 15px 20px;
      border-radius: 10px;
      font-family: 'Press Start 2P', monospace;
      font-size: 8px;
      max-width: 300px;
      box-shadow: 0 4px 15px rgba(0,0,0,0.3);
      z-index: 9999;
      animation: slideIn 0.3s ease-out;
    `;
    
    notification.innerHTML = `
      <div style="margin-bottom: 10px;">❌ GAME ERROR</div>
      <div style="font-size: 6px; margin-bottom: 10px;">${error.message || 'Unknown error occurred'}</div>
      <button onclick="this.parentElement.remove()" style="
        font-family: 'Press Start 2P', monospace;
        font-size: 6px;
        padding: 5px 10px;
        background: #DC143C;
        color: white;
        border: 1px solid #B22222;
        cursor: pointer;
      ">CLOSE</button>
    `;
    
    // Add slide-in animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideIn {
        from { transform: translateX(100%); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }
    `;
    document.head.appendChild(style);
    
    document.body.appendChild(notification);
    
    // Auto-remove after 10 seconds
    setTimeout(() => {
      if (notification.parentElement) {
        notification.remove();
      }
    }, 10000);
  }

  // === UTILITY METHODS ===

  getGameInfo() {
    return {
      launcher: 'ready',
      game: this.game ? this.game.getGameInfo() : null,
      browser: this.getBrowserInfo(),
      performance: this.getPerformanceInfo()
    };
  }

  getBrowserInfo() {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    
    return {
      userAgent: navigator.userAgent,
      webGL: !!gl,
      webGLRenderer: gl ? gl.getParameter(gl.RENDERER) : 'Not available',
      pixelRatio: window.devicePixelRatio || 1,
      touchSupport: 'ontouchstart' in window
    };
  }

  getPerformanceInfo() {
    return {
      memory: performance.memory ? {
        used: Math.round(performance.memory.usedJSHeapSize / 1048576) + ' MB',
        limit: Math.round(performance.memory.jsHeapSizeLimit / 1048576) + ' MB'
      } : 'Not available',
      timing: performance.timing ? {
        loadTime: performance.timing.loadEventEnd - performance.timing.navigationStart + ' ms'
      } : 'Not available'
    };
  }
}

function generatePWAIcons() {
  // Create 192x192 icon
  const canvas192 = document.createElement('canvas');
  canvas192.width = 192;
  canvas192.height = 192;
  const ctx192 = canvas192.getContext('2d');
  
  // Background
  ctx192.fillStyle = '#5C94FC';
  ctx192.fillRect(0, 0, 192, 192);
  
  // Wedding chapel emoji (simplified as text)
  ctx192.fillStyle = '#FFFFFF';
  ctx192.font = 'bold 120px Arial';
  ctx192.textAlign = 'center';
  ctx192.textBaseline = 'middle';
  ctx192.fillText('💒', 96, 96);
  
  // Create 512x512 icon  
  const canvas512 = document.createElement('canvas');
  canvas512.width = 512;
  canvas512.height = 512;
  const ctx512 = canvas512.getContext('2d');
  
  ctx512.fillStyle = '#5C94FC';
  ctx512.fillRect(0, 0, 512, 512);
  
  ctx512.fillStyle = '#FFFFFF';
  ctx512.font = 'bold 320px Arial';
  ctx512.textAlign = 'center';
  ctx512.textBaseline = 'middle';
  ctx512.fillText('💒', 256, 256);
  
  // Convert to blobs and create URLs
  canvas192.toBlob((blob) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('link');
    link.rel = 'icon';
    link.sizes = '192x192';
    link.href = url;
    document.head.appendChild(link);
  });
  
  canvas512.toBlob((blob) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('link'); 
    link.rel = 'icon';
    link.sizes = '512x512';
    link.href = url;
    document.head.appendChild(link);
  });
}

// === INITIALIZATION ===

// Debug info
console.log('🌐 Browser info:', {
  userAgent: navigator.userAgent,
  webGL: !!document.createElement('canvas').getContext('webgl'),
  touchSupport: 'ontouchstart' in window,
  pixelRatio: window.devicePixelRatio
});

// Start the game launcher
const launcher = new GameLauncher();

// Global access for debugging
if (GAME_CONFIG.DEBUG.CONSOLE_LOGGING) {
  window.gameLauncher = launcher;
  window.GAME_CONFIG = GAME_CONFIG;
  
  console.log('🛠️ Debug mode enabled');
  console.log('🛠️ Available debug objects: gameLauncher, GAME_CONFIG');
  console.log('🛠️ Game info: gameLauncher.getGameInfo()');
}

// Performance monitoring
if (GAME_CONFIG.DEBUG.SHOW_FPS) {
  console.log('📊 Performance monitoring enabled');
}

// Welcome message
console.log('💒 Welcome to Pablo & Bettina\'s Wedding Game!');
console.log('🎮 Built with retro 90s Mario-style charm');
console.log('🏆 Compete with other wedding guests for the high score!');

export default launcher;